import React, { useState, useEffect, useRef } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { FaLock, FaCheckCircle, FaTimesCircle, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_URL;
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;

export default function PasswordResetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordRules, setPasswordRules] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });
  const [showRules, setShowRules] = useState(false);
  const passwordRulesRef = useRef(null);
  const [confirmMatches, setConfirmMatches] = useState(true);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Verify token on component mount
  useEffect(() => {
    if (!token) {
      setErrorMsg('Invalid reset link. Please request a new password reset.');
      setIsVerifying(false);
      return;
    }

    // Ensure company code is configured for this client before verifying token
    if (!COMPANY_CODE) {
      setErrorMsg('Company code is not configured. Please contact support.');
      setIsVerifying(false);
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/customer/auth/verify-reset-token?token=${token}&company_code=${encodeURIComponent(COMPANY_CODE)}`);
      const data = await response.json();

      if (response.ok && data.success) {
        setTokenValid(true);
      } else {
        setErrorMsg(data.message || 'Invalid or expired reset link');
      }
    } catch (error) {
      console.error('Token verification error:', error);
      setErrorMsg('Failed to verify reset link');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error message when user starts typing
    if (errorMsg) setErrorMsg('');
    
    // Update password rules live when typing new password
    if (name === 'newPassword') {
      const rules = {
        length: value.length >= 8 && value.length <= 12,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /[0-9]/.test(value),
        specialChar: /[!@#$%^&*(),.?"':{}|<>\[\]\\/\\\\~`\-+=_;]/.test(value),
      };
      setPasswordRules(rules);
      // also update confirm match status when new password changes
      setConfirmMatches(value === formData.confirmPassword);
    }
    
    if (name === 'confirmPassword') {
      setConfirmMatches(value === formData.newPassword);
    }
  };

  const validatePassword = () => {
    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMsg('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;

    setIsLoading(true);
    setErrorMsg('');

    try {
      if (!COMPANY_CODE) {
        setErrorMsg('Company code is not configured. Cannot reset password.');
        setIsLoading(false);
        return;
      }
      const response = await fetch(`${BASE_URL}/api/customer/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_code : COMPANY_CODE,
          token: token,
          new_password: formData.newPassword
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResetSuccess(true);
        setMessage(data.message);
        // Clear form
        setFormData({ newPassword: '', confirmPassword: '' });
      } else {
        setErrorMsg(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setErrorMsg('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  // Redirect to login after successful reset (3s)
  useEffect(() => {
    if (resetSuccess) {
      const t = setTimeout(() => {
        navigate('/login');
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [resetSuccess, navigate]);

  if (isVerifying) {
    return (
      <div className="login-container">
        <Container fluid="xl">
          <Card className="login-card">
            <Card.Body className="p-3 p-md-5">
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Verifying reset link...</p>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="login-container">
      <Container fluid="xl">
        <Card className="login-card">
          <Card.Body className="p-3 p-md-5">
            <div className="text-center mb-4">
              <div className="login-icon mb-3">
                <FaLock size={48} className={resetSuccess ? "text-success" : "text-primary"} />
              </div>
              <h2 className="login-title">
                {resetSuccess ? 'Password Reset Successful!' : 'Reset Your Password'}
              </h2>
              <p className="login-subtitle">
                {resetSuccess 
                  ? 'Your password has been successfully updated' 
                  : 'Enter your new password below'
                }
              </p>
            </div>

            {message && (
              <Alert variant="success" className="modern-alert">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <FaCheckCircle className="me-2" />
                  <strong>Success!</strong>
                </div>
                {message}
              </Alert>
            )}

            {errorMsg && (
              <Alert variant="danger" className="modern-alert">
                <div className="d-flex align-items-center justify-content-center mb-2">
                  <FaTimesCircle className="me-2" />
                  <strong>Error</strong>
                </div>
                {errorMsg}
              </Alert>
            )}

            <Row className="justify-content-center">
              <Col lg={6} md={8}>
                {!tokenValid && !resetSuccess ? (
                  // Invalid token view
                  <div className="text-center">
                    <p className="mb-4">
                      The reset link is invalid or has expired. Please request a new password reset.
                    </p>
                    <Button 
                      variant="primary" 
                      onClick={handleBackToLogin}
                      className="me-2"
                    >
                      <FaArrowLeft className="me-2" />
                      Back to Login
                    </Button>
                  </div>
                ) : resetSuccess ? (
                  // Success view
                  <div className="text-center">
                    <p className="mb-4">
                      You can now log in with your new password.
                    </p>
                    <Button 
                      variant="primary" 
                      size="lg"
                      onClick={handleBackToLogin}
                    >
                      Continue to Login
                    </Button>
                  </div>
                ) : (
                  // Reset form view
                  <div className="form-section">
                    <Form className="login-form" onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label className="form-label">New Password</Form.Label>
                        <div className="password-input-wrapper">
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="newPassword"
                            value={formData.newPassword}
                              onChange={handleChange}
                              onFocus={() => setShowRules(true)}
                              onBlur={() => setShowRules(false)}
                            placeholder="Enter your new password"
                            className="modern-input password-input"
                            required
                            minLength={6}
                            autoComplete="new-password"
                            spellCheck={false}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="password-toggle-btn"
                            title={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                          {showRules && (
                            <div className='password-rules' ref={passwordRulesRef}>
                              <small className="rules-title">Password requirements:</small>
                              <ul className='requirements-list'>
                                <li className={passwordRules.length ? 'valid' : 'invalid'}>
                                  {passwordRules.length ? '✓' : '✗'} 8-12 characters long
                                </li>
                                <li className={passwordRules.uppercase ? 'valid' : 'invalid'}>
                                  {passwordRules.uppercase ? '✓' : '✗'} One uppercase letter (A-Z)
                                </li>
                                <li className={passwordRules.lowercase ? 'valid' : 'invalid'}>
                                  {passwordRules.lowercase ? '✓' : '✗'} One lowercase letter (a-z)
                                </li>
                                <li className={passwordRules.number ? 'valid' : 'invalid'}>
                                  {passwordRules.number ? '✓' : '✗'} One number (0-9)
                                </li>
                                <li className={passwordRules.specialChar ? 'valid' : 'invalid'}>
                                  {passwordRules.specialChar ? '✓' : '✗'} One special character (!@#$...)
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">Confirm New Password</Form.Label>
                        <div className="password-input-wrapper">
                          <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm your new password"
                            className="modern-input password-input"
                            required
                            autoComplete="new-password"
                            spellCheck={false}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="password-toggle-btn"
                            title={showConfirmPassword ? "Hide password" : "Show password"}
                          >
                            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                        <Form.Text className={confirmMatches ? 'text-success' : 'text-danger'}>
                          {formData.confirmPassword.length === 0 ? '' : (confirmMatches ? 'Passwords match' : 'Passwords do not match')}
                        </Form.Text>
                      </Form.Group>
                      
                      <div className="form-actions text-center">
                        <Button 
                          type="submit" 
                          className="login-btn mb-3"
                          disabled={isLoading}
                          size="lg"
                        >
                          {isLoading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Resetting Password...
                            </>
                          ) : (
                            <>
                              <FaLock className="me-2" />
                              Reset Password
                            </>
                          )}
                        </Button>
                        
                        <div>
                          <Button 
                            variant="link" 
                            onClick={handleBackToLogin}
                            disabled={isLoading}
                            className="text-decoration-none"
                          >
                            <FaArrowLeft className="me-1" />
                            Back to Login
                          </Button>
                        </div>
                      </div>
                    </Form>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}